import React from "react";
import type { Project } from "../../../../api/useProjects";
import SidePanelLayout from "@ui/SidePanel/Layout";
import Evolution from "./Evolution";
import { useSetups } from "../../../../api/useSetups";
import { useProjectSource } from "../../../../api/useSources";
import useActiveAnalysesStream from "../../../../api/useActiveAnalysesStream";
import { useIndexedFailureValues } from "../../../../api/useIndexedFailureValues";
import { useIndexedManualFailureValues } from "../../../../api/useManualFailureValues";
import { isNonEmptyArray } from "diagnostics-lib/array";
import ScreenGraph from "@ui/Picto/ScreenGraph";
import t from "../../../../i18n";
import { withKeyNotNull } from "diagnostics-lib/typescript";
import { comparatorBy } from "diagnostics-lib/comparator";
import useSelectedAnalyses from "../../Analysis/useSelectedAnalyses";
import useDebounce from "@ui/lib/hooks/useDebounce";
import { styled } from "@linaria/react";
import { Navigate, useNavigate, useOutletContext } from "react-router";
import { AnalysisDateTitle } from "../../AnalysisDateTitle";
import ProcessIndicators from "./ProcessIndicators";
import { css } from "@linaria/core";
import Losses from "./Losses";
import Instrumentation from "./Instrumentation";
import Failures from "./Failures";
import BlankStateV2 from "@ui/BlankStateV2";
import { useQuantities } from "../../../../api/useQuantities";
import { toPath } from "../../../../router/helpers/path";
import { useSettings } from "../../../../api/useSettings";

const Synthesis = () => {
  const project = useOutletContext<Project>();
  if (!project) throw new Error("no project");

  const settings = useSettings();
  const navigate = useNavigate();
  const source = useProjectSource(project);
  const { activeAnalyses } = useActiveAnalysesStream(project.id);
  const quantities = useQuantities(project.id);
  const setups = useSetups(project.id);
  const indexedFailureValues = useIndexedFailureValues(project.id);
  const indexedManualFailureValues = useIndexedManualFailureValues(project.id);

  const { selectedAnalyses } = useSelectedAnalyses(project.id, activeAnalyses);
  const debouncedSelectedAnalyses = React.useDeferredValue(useDebounce(selectedAnalyses, 1000));

  const quantitiesOfInterest = React.useMemo(
    () => quantities.filter(withKeyNotNull("qoiOrder")).sort(comparatorBy("qoiOrder")),
    [quantities],
  );

  const processIndicators = React.useMemo(
    () =>
      quantities
        .filter(withKeyNotNull("processIndicatorOrder"))
        .sort(comparatorBy("processIndicatorOrder")),
    [quantities],
  );

  if (settings.HIDE_LEGACY_SUMMARY_PAGE) {
    return <Navigate to={toPath("/projects/:projectId/summary", { projectId: project.id })} />;
  }

  if (quantitiesOfInterest.length === 0) {
    return (
      <BlankStateV2
        picto={<ScreenGraph />}
        title={t("summary.empty.title")}
        onClick={() =>
          navigate(
            toPath("/projects/:projectId/settings", {
              projectId: project.id,
            }),
          )
        }
        buttonLabel={t("summary.empty.action")}
      />
    );
  }

  if (!isNonEmptyArray(activeAnalyses) || !source)
    return (
      <BlankStateV2
        picto={<ScreenGraph />}
        title={t("common.empty.activeAnalyses.title")}
        subtitle={t("common.empty.activeAnalyses.description")}
        onClick={() =>
          navigate(
            toPath("/projects/:projectId/measures", {
              projectId: project.id,
            }),
          )
        }
        buttonLabel={t("common.empty.activeAnalyses.action")}
      />
    );

  return (
    <SidePanelLayoutNoFlex>
      <Evolution
        project={project}
        source={source}
        setups={setups}
        activeAnalyses={activeAnalyses}
        indexedFailureValues={indexedFailureValues}
        indexedManualFailureValues={indexedManualFailureValues}
        quantitiesOfInterest={quantitiesOfInterest}
      />
      {debouncedSelectedAnalyses[0] && debouncedSelectedAnalyses.length === 1 ? (
        <>
          <AnalysisDateTitle
            className={PaddingTop}
            event={null}
            analysis={debouncedSelectedAnalyses[0]}
            timeZone={project.timezone}
          />
          <div className={`${Grid} ${processIndicators.length === 0 ? GridCWithoutPI : GridC}`}>
            <Losses
              project={project}
              setups={setups}
              activeAnalysis={debouncedSelectedAnalyses[0]}
              quantities={quantities}
              quantitiesOfInterest={quantitiesOfInterest}
            />
            {processIndicators.length > 0 && (
              <ProcessIndicators
                project={project}
                activeAnalyses={debouncedSelectedAnalyses[0]}
                processIndicators={processIndicators}
              />
            )}
            <Instrumentation
              projectId={project.id}
              analysis={debouncedSelectedAnalyses[0]}
              quantities={quantities}
            />
            <Failures
              project={project}
              setups={setups}
              activeAnalysis={debouncedSelectedAnalyses[0]}
              quantitiesOfInterest={quantitiesOfInterest}
            />
          </div>
        </>
      ) : null}
    </SidePanelLayoutNoFlex>
  );
};

export default React.memo(Synthesis);

//#region CSS
const PaddingTop = css`
  padding-top: 20px;
`;

const SidePanelLayoutNoFlex = styled(SidePanelLayout)`
  flex: none;
`;

const breakpoints = {
  medium: "min-width: 1280px",
  large: "min-width: 1920px",
} as const;

const layoutGap = 10;

const Grid = css`
  display: flex;
  flex-direction: column;
  gap: ${layoutGap}px;
  & > :nth-child(1) {
    order: 1;
  }
  & > :nth-child(2) {
    order: 3;
  }
  & > :nth-child(3) {
    order: 2;
  }
`;

const GridC = css`
  @media (${breakpoints.medium}) {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    grid-template-rows: max-content 1fr;
    grid-gap: ${layoutGap}px;

    & > :nth-child(1) {
      grid-area: 1 / 1 / 2 / 5;
    }
    & > :nth-child(2) {
      grid-area: 1 / 5 / 2 / 9;
    }
    & > :nth-child(3) {
      grid-area: 2 / 6 / 3 / 9;
    }
    & > :nth-child(4) {
      grid-area: 2 / 1 / 3 / 6;
    }
  }
  @media (${breakpoints.large}) {
    grid-template-columns: repeat(9, 1fr);

    & > :nth-child(1) {
      grid-area: 1 / 1 / 2 / 4;
    }
    & > :nth-child(2) {
      grid-area: 1 / 4 / 2 / 7;
    }
    & > :nth-child(3) {
      grid-area: 1 / 7 / 3 / 10;
    }
    & > :nth-child(4) {
      grid-area: 2 / 1 / 3 / 7;
    }
  }
`;
const GridCWithoutPI = css`
  @media (${breakpoints.medium}) {
    display: grid;
    grid-template-columns: repeat(8, 1fr);
    grid-template-rows: max-content 1fr;
    grid-gap: ${layoutGap}px;

    & > :nth-child(1) {
      grid-area: 1 / 1 / 2 / 5;
    }
    & > :nth-child(2) {
      grid-area: 1 / 5 / 2 / 9;
    }
    & > :nth-child(3) {
      grid-area: 2 / 1 / 3 / 9;
    }
  }
  @media (${breakpoints.large}) {
    grid-template-columns: repeat(8, 1fr);

    & > :nth-child(1) {
      grid-area: 1 / 1 / 2 / 5;
    }
    & > :nth-child(2) {
      grid-area: 1 / 5 / 2 / 9;
    }
    & > :nth-child(3) {
      grid-area: 2 / 1 / 3 / 6;
    }
  }
`;
//#endregion CSS
